import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  FormControl,
  Autocomplete,
  TextField,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import TuneIcon from "@mui/icons-material/Tune";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

import PageBody from "../GeneralComponents/PageBody";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import InputFilter from "../GeneralComponents/InputFilter";

/* =========================================
   Constantes dominio
========================================= */

const TECNOLOGIAS = [
  "FIBRA ÓPTICA",
  "SATELITAL",
  "SATELITE BAJA ALTURA",
  "ADSL_HFC",
  "HFC & FTTH",
  "HFC",
  "FTTH",
  "4G",
  "MICROONDAS",
  "VSAT",
];

const OPERADORES = {
  "LUZLINARES S.A.": 1,
  "MI INTERNET SPA": 2,
  "ELECTRONET S.A.": 3,
  "GTD MANQUEHUE S.A.": 4,
  "INGBELL CHILE SPA": 5,
  "CHILE TV CABLE S.A.": 6,
  "NETLAND CHILE S.A.": 7,
  "TELEFONICA EMPRESAS CHILE S.A.": 8,
  "LUZPARRAL S.A.": 11,
  "TEXUS NETWORKS SPA.": 19,
  "VTR COMUNICACIONES SPA.": 20,
};

const SCHEMA = {
  columns: [
    {
      key: "convocatoria",
      label: "Convocatoria",
      type: "string",
      required: true,
      maxLength: 20,
      pattern: "^(CPE\\d{4}-[1-9]\\d*|NYA\\s?\\d{4}|Conv-\\d{4}-\\d+)$",
    },
    { key: "año", label: "Año", type: "integer", required: true, min: 2024, max: 2030 },
    { key: "etapa", label: "Etapa", type: "integer", required: true, enum: [1, 2, 3] },
    { key: "bloque", label: "Bloque", type: "integer", required: true, enum: [1, 2, 3] },
    {
      key: "operador",
      label: "Operador",
      type: "string",
      required: true,
      enum: Object.keys(OPERADORES),
      maxLength: 80,
    },
    { key: "rbd", label: "RBD", type: "integer", required: true, min: 1, max: 99999999 },
    {
      key: "nombre_establecimiento",
      label: "Nombre establecimiento",
      type: "string",
      required: true,
      maxLength: 120,
    },
    {
      key: "tipo_establecimiento",
      label: "Tipo establecimiento",
      type: "string",
      required: true,
      enum: [
        "ADMINISTRACION DELEGADA",
        "MUNICIPAL DAEM",
        "PARTICULAR SUBVENCIONADO",
        "SERVICIO LOCAL DE EDUCACIÓN (SLE)",
        "SERVICIO LOCAL DE EDUCACIÓN PÚBLICA (SLEP)",
      ],
      maxLength: 120,
    },
    {
      key: "rut_sostenedor",
      label: "RUT Sostenedor",
      type: "string",
      required: true,
      pattern: "^[0-9]{7,9}(-[0-9Kk])?$",
      maxLength: 12,
    },
    {
      key: "nombre_sostenedor",
      label: "Nombre sostenedor",
      type: "string",
      required: true,
      maxLength: 120,
    },
    { key: "region", label: "Región", type: "string", required: true, maxLength: 40 },
    { key: "comuna", label: "Comuna", type: "string", required: true, maxLength: 60 },
    { key: "direccion", label: "Dirección", type: "string", required: true, maxLength: 140 },
    {
      key: "tipo_de_area",
      label: "Tipo de área",
      type: "string",
      required: true,
      enum: ["URBANA", "RURAL"],
    },
    { key: "latitud", label: "Latitud", type: "number", required: true, min: -90, max: 90 },
    { key: "longitud", label: "Longitud", type: "number", required: true, min: -180, max: 180 },
    { key: "zona", label: "Zona", type: "integer", required: false, min: 0 },
    { key: "matricula_total", label: "Matrícula total", type: "integer", required: false, min: 0 },
    {
      key: "jornada",
      label: "Jornada",
      type: "string",
      required: true,
      maxLength: 80,
      enum: ["A", "B", "C", "D", "E"],
    },
    { key: "bajada_total_mbps", label: "Bajada total [Mbps]", type: "number", required: true, min: 0 },
    { key: "subida_total_mbps", label: "Subida total [Mbps]", type: "number", required: true, min: 0 },
    {
      key: "bajada_internacional_mbps",
      label: "Bajada internacional [Mbps]",
      type: "number",
      required: true,
      min: 0,
    },
    {
      key: "disponibilidad_mensual",
      label: "Disponibilidad mensual",
      type: "number",
      required: true,
      min: 0,
      max: 100,
    },
    {
      key: "tipo_tecnologia",
      label: "Tipo tecnología",
      type: "string",
      required: true,
      enum: TECNOLOGIAS,
    },
    { key: "subsidio_anual", label: "Subsidio anual", type: "integer", required: true, min: 0 },
    { key: "subsidio_mensualizado", label: "Subsidio mensualizado", type: "integer", required: true, min: 0 },
    {
      key: "telefono",
      label: "Teléfono",
      type: "string",
      required: false,
      maxLength: 20,
      pattern: "^\\+?[0-9]{6,15}$",
    },
    {
      key: "email",
      label: "Email",
      type: "string",
      required: false,
      maxLength: 120,
      pattern: "^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$",
    },
  ],
};

const OPERADOR_COL = SCHEMA.columns.find((c) => c.key === "operador");
const ALLOWED_OPERADORES = OPERADOR_COL?.enum ?? [];

/* =========================================
   Helpers validación
========================================= */

function trimEdges(val) {
  if (val === null || val === undefined) return val;
  if (typeof val === "number") return val;
  return String(val).trim();
}

function parseNumericFlexible(raw) {
  if (raw === null || raw === undefined) return { value: raw };
  let s = String(raw).trim();
  s = s.replace(/^\s*\$/g, "").replace(/%/g, "").replace(/\s+/g, "");
  if (s.includes(".") && s.includes(",")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (s.includes(",") && !s.includes(".")) {
    s = s.replace(",", ".");
  }
  s = s.replace(/'/g, "");
  const num = Number(s);
  if (Number.isNaN(num)) return { value: raw, error: "Debe ser numérico" };
  return { value: num };
}

function parseIntegerFlexible(raw) {
  if (raw === null || raw === undefined) return { value: raw };
  let s = String(raw).trim();
  s = s.replace(/^\s*\$/g, "").replace(/%/g, "").replace(/\s+/g, "");
  s = s.replace(/[.,' ]/g, "");
  const m = s.match(/^\d+$/);
  if (!m) return { value: raw, error: "Debe ser entero" };
  return { value: Number(s) };
}

function coerce(val, type) {
  if (type === "integer") {
    const { value, error } = parseIntegerFlexible(val);
    if (error) return { value, error };
    return { value };
  }
  if (type === "number") {
    const { value, error } = parseNumericFlexible(val);
    if (error) return { value, error };
    return { value };
  }
  return { value: trimEdges(val) };
}

function validateCell(raw, col) {
  const errors = [];
  const isEmpty = raw === null || raw === undefined || String(raw).trim() === "";

  if (col.required && isEmpty) {
    errors.push("Requerido");
    return errors;
  }

  if (isEmpty) return errors;

  const { value, error } = coerce(raw, col.type);
  if (error) errors.push(error);

  if (col.type === "string") {
    const len = String(value).length;
    if (col.minLength != null && len < col.minLength) errors.push(`Mín ${col.minLength} car.`);
    if (col.maxLength != null && len > col.maxLength) errors.push(`Máx ${col.maxLength} car.`);
  }

  if (col.type === "number" || col.type === "integer") {
    const num = Number(value);
    if (col.min != null && num < col.min) errors.push(`Mín ${col.min}`);
    if (col.max != null && num > col.max) errors.push(`Máx ${col.max}`);
  }

  if (col.enum && col.enum.length > 0) {
    const allowed = col.enum.map(String);
    if (!allowed.includes(String(value))) errors.push("Valor no permitido");
  }

  if (col.pattern) {
    try {
      if (!new RegExp(col.pattern).test(String(value))) {
        errors.push("Formato inválido");
      }
    } catch {}
  }

  return errors;
}

function validateRow(row, schema) {
  const e = {};
  for (const col of schema.columns) {
    const list = validateCell(row[col.key], col);
    if (list.length) e[col.key] = list;
  }
  return e;
}

function normalizeOperatorName(str) {
  if (!str) return "";
  return String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[.,]/g, " ")
    .replace(/\b(SA|S\.A\.|SPA|S\.P\.A\.|LTDA|LIMITADA)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function findBestOperatorMatch(raw) {
  if (!raw) return raw;

  const norm = normalizeOperatorName(raw);
  if (!norm) return raw;

  let bestOp = null;
  let bestScore = 0;

  for (const op of ALLOWED_OPERADORES) {
    const normOp = normalizeOperatorName(op);
    let score = 0;

    if (norm === normOp) {
      score = 1;
    } else if (normOp.includes(norm) || norm.includes(normOp)) {
      score = 0.9;
    } else {
      const tokens = norm.split(" ");
      const opTokens = normOp.split(" ");
      const common = tokens.filter((t) => opTokens.includes(t));
      if (common.length > 0) {
        score = common.length / Math.max(tokens.length, opTokens.length);
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestOp = op;
    }
  }

  if (bestScore >= 0.4 && bestOp) return bestOp;
  return raw;
}

const BEST_MATCHERS = {
  operador: findBestOperatorMatch,
};

function castRowTypes(row, schema) {
  const out = { ...row };

  for (const col of schema.columns) {
    if (col.type === "number" || col.type === "integer") {
      const { value } = coerce(out[col.key], col.type);
      out[col.key] = value;
    } else if (col.type === "string") {
      out[col.key] = trimEdges(out[col.key]);
    }

    if (BEST_MATCHERS[col.key]) {
      out[col.key] = BEST_MATCHERS[col.key](out[col.key]);
    }
  }

  return out;
}

function normalizeRbd(value) {
  if (value == null) return "";
  const cleaned = String(value).replace(/\D/g, "");
  if (!cleaned) return "";
  return cleaned.padStart(4, "0");
}

/* =========================================
   RBD chip select local
========================================= */

function RbdChipSelect({
  item,
  name,
  value = [],
  opt = [],
  callback,
  icon,
  hideClearButton = false,
  showLeftBorder = false,
}) {
  const [selectedValues, setSelectedValues] = useState((value || []).map((v) => String(v)));
  const [selectedOption, setSelectedOption] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    setSelectedValues((value || []).map((v) => String(v)));
  }, [value]);

  const options = useMemo(() => {
    const unique = Array.from(new Set((opt || []).map((v) => String(v))));
    return unique.filter((v) => !selectedValues.includes(v));
  }, [opt, selectedValues]);

  const handleAddClick = () => {
    const typedValue = normalizeRbd(inputValue);
    const optionValue = selectedOption == null ? "" : normalizeRbd(selectedOption);
    const valueToAdd = optionValue || typedValue;

    if (valueToAdd && !selectedValues.includes(valueToAdd)) {
      const next = [...selectedValues, valueToAdd];
      setSelectedValues(next);
      callback?.(item, next);
    }

    setSelectedOption(null);
    setInputValue("");
  };

  const handleDeleteChip = (chipToDelete) => {
    const next = selectedValues.filter((v) => v !== chipToDelete);
    setSelectedValues(next);
    callback?.(item, next);
  };

  const handleClearClick = () => {
    setSelectedValues([]);
    callback?.(item, []);
    setSelectedOption(null);
    setInputValue("");
  };

  return (
    <Box sx={{ ...(showLeftBorder ? { borderLeft: "1px solid #B4B4B4", pl: 1 } : {}), pb: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <FormControl sx={{ flexGrow: 1, minHeight: "36px" }} size="small">
          <Autocomplete
            freeSolo
            size="small"
            value={selectedOption}
            onChange={(event, newValue) => setSelectedOption(newValue)}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
            options={options}
            getOptionLabel={(optVal) => (optVal == null ? "" : String(optVal))}
            isOptionEqualToValue={(optVal, val) => String(optVal) === String(val)}
            sx={{
              padding: 0,
              minHeight: "28px",
              "& .MuiInputBase-root": {
                minHeight: "32px",
                height: "32px",
                fontSize: "0.75rem",
                paddingRight: "8px",
              },
              "& .MuiOutlinedInput-input": { padding: "4px 8px" },
              "& .MuiAutocomplete-endAdornment": { top: "calc(50% - 0px)" },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={name}
                InputLabelProps={{
                  shrink: isFocused || inputValue.length > 0,
                  sx: {
                    marginLeft: inputValue.length > 0 || isFocused ? 0 : "28px",
                    fontSize: "0.75rem",
                  },
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
                      {icon ?? <TuneIcon />}
                      {params.InputProps.startAdornment}
                    </Box>
                  ),
                }}
              />
            )}
          />
        </FormControl>

        <Button
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleAddClick}
          size="small"
          sx={{ color: "#1976D2", whiteSpace: "nowrap" }}
        >
          Agregar a la lista
        </Button>

        {!hideClearButton && (
          <Button
            startIcon={<RemoveCircleOutlineIcon />}
            onClick={handleClearClick}
            size="small"
            sx={{ color: "#D84315", whiteSpace: "nowrap" }}
          >
            Limpiar filtro
          </Button>
        )}
      </Stack>

      <Box
        sx={{
          border: "1px solid #999",
          mt: 0.5,
          px: 1,
          py: 0.2,
          borderRadius: 1.5,
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          minHeight: "32px",
          alignItems: "center",
        }}
      >
        {selectedValues.length > 0 ? (
          selectedValues.map((valueItem) => (
            <Chip
              key={valueItem}
              label={valueItem}
              onDelete={() => handleDeleteChip(valueItem)}
              size="small"
              sx={{ fontSize: 13 }}
            />
          ))
        ) : (
          <Box sx={{ color: "#888", fontSize: 13 }}>Sin elementos seleccionados</Box>
        )}
      </Box>
    </Box>
  );
}

/* =========================================
   Data dummy
========================================= */

const BASE_ROWS = [
  {
    convocatoria: "Conv-2024-1",
    año: 2025,
    etapa: 1,
    bloque: 1,
    operador: "CHILE TV CABLE S.A.",
    rbd: 340,
    nombre_establecimiento: "ESCUELA PANGAL DEL LAJA",
    tipo_establecimiento: "MUNICIPAL DAEM",
    rut_sostenedor: "69041500-0",
    nombre_sostenedor: "CORPORACIÓN EDUCACIONAL NAHUEN DEL CHAPA",
    region: "De Coquimbo",
    comuna: "La Laja",
    direccion: "Camino interior s/n",
    tipo_de_area: "RURAL",
    latitud: -30.123,
    longitud: -71.456,
    zona: 13,
    matricula_total: 120,
    jornada: "A",
    bajada_total_mbps: 200,
    subida_total_mbps: 100,
    bajada_internacional_mbps: 80,
    disponibilidad_mensual: 99.5,
    tipo_tecnologia: "FIBRA ÓPTICA",
    subsidio_anual: 1200000,
    subsidio_mensualizado: 100000,
    telefono: "+56912345678",
    email: "contacto@colegio.cl",
    accion: "Recepción de Obras",
  },
  {
    convocatoria: "Conv-2024-1",
    año: 2025,
    etapa: 1,
    bloque: 1,
    operador: "CHILE TV CABLE",
    rbd: 341,
    nombre_establecimiento: "ILUSTRE MUNICIPALIDAD DE EL CARMEN",
    tipo_establecimiento: "MUNICIPAL DAEM",
    rut_sostenedor: "6904150-0",
    nombre_sostenedor: "CORPORACIÓN EDUCACIONAL NAHUEN DEL CHAPA",
    region: "De Ñuble",
    comuna: "El Carmen",
    direccion: "Dirección inválida demo",
    tipo_de_area: "RURAL",
    latitud: -36.9,
    longitud: -72.1,
    zona: 13,
    matricula_total: 80,
    jornada: "A",
    bajada_total_mbps: 150,
    subida_total_mbps: 50,
    bajada_internacional_mbps: 25,
    disponibilidad_mensual: 97.2,
    tipo_tecnologia: "FIBRA ÓPTICA",
    subsidio_anual: 900000,
    subsidio_mensualizado: 75000,
    telefono: "+56911111111",
    email: "demo@colegio.cl",
    accion: "Inicio de Servicio",
  },
  {
    convocatoria: "Conv-2024-1",
    año: 2025,
    etapa: 1,
    bloque: 1,
    operador: "CHILE TV CABLE S.A.",
    rbd: 342,
    nombre_establecimiento: "ESCUELA PANGAL DEL LAJA",
    tipo_establecimiento: "MUNICIPAL DAEM",
    rut_sostenedor: "69041500-0",
    nombre_sostenedor: "CORPORACIÓN EDUCACIONAL NAHUEN DEL CHAPA",
    region: "De Coquimbo",
    comuna: "La Laja",
    direccion: "Camino interior s/n",
    tipo_de_area: "RURAL",
    latitud: -30.123,
    longitud: -71.456,
    zona: 13,
    matricula_total: 120,
    jornada: "A",
    bajada_total_mbps: 200,
    subida_total_mbps: 100,
    bajada_internacional_mbps: 80,
    disponibilidad_mensual: 99.5,
    tipo_tecnologia: "FIBRA ÓPTICA",
    subsidio_anual: 1200000,
    subsidio_mensualizado: 100000,
    telefono: "+56912345678",
    email: "contacto@colegio.cl",
    accion: "Recepción de Obras",
  },
  {
    convocatoria: "Conv-2024-1",
    año: 2025,
    etapa: 1,
    bloque: 1,
    operador: "CHILE TV CABLE S.A.",
    rbd: 343,
    nombre_establecimiento: "ESCUELA PANGAL DEL LAJA",
    tipo_establecimiento: "MUNICIPAL DAEM",
    rut_sostenedor: "69041500-0",
    nombre_sostenedor: "CORPORACIÓN EDUCACIONAL NAHUEN DEL CHAPA",
    region: "De Coquimbo",
    comuna: "La Laja",
    direccion: "Camino interior s/n",
    tipo_de_area: "RURAL",
    latitud: -30.123,
    longitud: -71.456,
    zona: 13,
    matricula_total: 120,
    jornada: "A",
    bajada_total_mbps: 200,
    subida_total_mbps: 100,
    bajada_internacional_mbps: 80,
    disponibilidad_mensual: 99.5,
    tipo_tecnologia: "FIBRA ÓPTICA",
    subsidio_anual: 1200000,
    subsidio_mensualizado: 100000,
    telefono: "+56912345678",
    email: "contacto@colegio.cl",
    accion: "Recepción de Obras",
  },
];

/* =========================================
   Helpers tabla
========================================= */

function makeNotFoundRow(rbd, filters) {
  return {
    convocatoria: filters.convocatoria || "",
    año: 2025,
    etapa: "",
    bloque: "",
    operador: filters.operador || "",
    rbd: Number(String(rbd).replace(/\D/g, "")) || rbd,
    nombre_establecimiento: "",
    tipo_establecimiento: "",
    rut_sostenedor: "",
    nombre_sostenedor: "",
    region: "",
    comuna: "",
    direccion: "",
    tipo_de_area: "",
    latitud: "",
    longitud: "",
    zona: filters.zona_num ?? "",
    matricula_total: "",
    jornada: "",
    bajada_total_mbps: "",
    subida_total_mbps: "",
    bajada_internacional_mbps: "",
    disponibilidad_mensual: "",
    tipo_tecnologia: "",
    subsidio_anual: "",
    subsidio_mensualizado: "",
    telefono: "",
    email: "",
    accion: "-",
    no_encontrado: true,
  };
}

function validateReviewRow(row) {
  if (row.no_encontrado) {
    return { _row: ["No hay registros del RBD"] };
  }
  return validateRow(castRowTypes(row, SCHEMA), SCHEMA);
}

function buildComentarioAcciones(row, rowErrors) {
  if (row.no_encontrado) return "No hay registros del RBD";
  if (Object.keys(rowErrors).length > 0) return "contiene errores o falta información del RBD";
  return "Recepción de Obras: OK; Inicio de Servicio: Pendiente, Facturación: Pendiente";
}

function formatCellValue(value, key) {
  if (value === null || value === undefined || value === "") return "-";
  if (key === "rbd") return normalizeRbd(value);
  return String(value);
}

/* =========================================
   Componente principal
========================================= */

export default function RevisorRBD() {
  const [filters, setFilters] = useState({
    convocatoria: "Conv-2024-1",
    zona: "Zona 13",
    accion: null,
    operador: null,
    rbds: ["0340", "0341", "0342", "0343", "0344"],
  });

  const handleFilterChange = (item, value) => {
    setFilters((prev) => ({ ...prev, [item]: value }));
  };

  const convocatorias = useMemo(
    () => Array.from(new Set(BASE_ROWS.map((r) => r.convocatoria))),
    []
  );

  const zonas = ["Zona 13", "Zona 14", "Zona 20"];
  const acciones = ["Recepción de Obras", "Inicio de Servicio"];
  const operadores = Object.keys(OPERADORES);
  const rbdOptions = useMemo(
    () => Array.from(new Set(BASE_ROWS.map((r) => normalizeRbd(r.rbd)))).sort(),
    []
  );

  const tableColumns = useMemo(() => {
    return [
      { key: "__estado__", label: "" },
      { key: "__comentario__", label: "Comentario de acciones" },
      ...SCHEMA.columns.map((col) => ({
        key: col.key,
        label: col.label,
      })),
    ];
  }, []);

  const tableRows = useMemo(() => {
    const zonaNum = Number(String(filters.zona || "").replace(/\D/g, "")) || null;

    const filteredBase = BASE_ROWS.filter((row) => {
      if (filters.convocatoria && row.convocatoria !== filters.convocatoria) return false;
      if (zonaNum != null && Number(row.zona) !== zonaNum) return false;
      if (filters.accion && row.accion !== filters.accion) return false;
      if (filters.operador && row.operador !== filters.operador) return false;
      return true;
    });

    const byRbd = new Map(
      filteredBase.map((row) => [normalizeRbd(row.rbd), castRowTypes(row, SCHEMA)])
    );

    return (filters.rbds || []).map((rbd) => {
      const key = normalizeRbd(rbd);
      return byRbd.get(key) || makeNotFoundRow(key, { ...filters, zona_num: zonaNum });
    });
  }, [filters]);

  const rowsWithValidation = useMemo(() => {
    return tableRows.map((row) => {
      const rowErrors = validateReviewRow(row);
      return {
        row,
        rowErrors,
        hasError: Object.keys(rowErrors).length > 0,
        comentario: buildComentarioAcciones(row, rowErrors),
      };
    });
  }, [tableRows]);

  return (
    <PageBody>
      <PageBgBlock className="card">
        <Stack spacing={3}>
          <Stack spacing={1} alignItems="center">
            <Typography
              variant="h1"
              sx={{
                textAlign: "center",
                fontWeight: 700,
                color: "#3F3A3A",
              }}
            >
              Revisor de RBD
            </Typography>

            <Typography
              variant="body1"
              sx={{
                maxWidth: "860px",
                textAlign: "center",
                color: "#4B4B4B",
              }}
            >
              A continuación puede agregar a la lista los RBD que necesite corroborar si se
              encuentran dentro de nuestras bases de datos, podrá ver todos los campos de
              información con los que deben contar para darles Recepción de Obras o Inicio
              de Servicio.
            </Typography>
          </Stack>

          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, md: 3 }}>
              <InputFilter
                item="convocatoria"
                name="Convocatoria"
                icon={<TuneIcon />}
                opt={convocatorias}
                value={filters.convocatoria}
                callback={handleFilterChange}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <InputFilter
                item="zona"
                name="Zona"
                icon={<TuneIcon />}
                opt={zonas}
                value={filters.zona}
                callback={handleFilterChange}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <RbdChipSelect
                item="rbds"
                name="RBD"
                icon={<TuneIcon />}
                opt={rbdOptions}
                value={filters.rbds}
                callback={handleFilterChange}
                showLeftBorder={false}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <InputFilter
                item="accion"
                name="Acciones"
                icon={<TuneIcon />}
                opt={acciones}
                value={filters.accion}
                callback={handleFilterChange}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <InputFilter
                item="operador"
                name="Operador"
                icon={<TuneIcon />}
                opt={operadores}
                value={filters.operador}
                callback={handleFilterChange}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.82rem",
                  color: "#4F4F4F",
                  pt: { xs: 0, md: 1.5 },
                }}
              >
                <b>¿Aún no ve el RBD que necesita?</b> búsquelo en el filtro por único RBD,
                ya que puede que no pertenezca a los filtros buscados o no se encuentre en
                nuestros registros.
              </Typography>
            </Grid>
          </Grid>
        </Stack>
      </PageBgBlock>

      <Box sx={{ pt: 3 }}>
        <PageBgBlock>
          <Box
          >
            <Box
              sx={{
                border: "1px solid #BDBDBD",
                backgroundColor: "#EFEFEF",
                color: "#4A4A4A",
                fontWeight: 700,
                textAlign: "center",
                py: 1.2,
                px: 2,
                mb: 0,
                borderBottom: "none",
              }}
            >
              DETALLE DE BÚSQUEDA
            </Box>

            <Box
              sx={{
                border: "1px solid #BDBDBD",
                borderTop: "none",
                backgroundColor: "#FFF",
                overflow: "hidden",
              }}
            >
              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: "65vh" }}>
                <Table size="small" stickyHeader sx={{ minWidth: 3200 }}>
                  <TableHead>
                    <TableRow>
                      {tableColumns.map((col, index) => (
                        <TableCell
                          key={col.key}
                          sx={{
                            backgroundColor: "#D7D7D7",
                            fontWeight: 700,
                            borderRight: index === tableColumns.length - 1 ? "none" : "1px solid #BDBDBD",
                            minWidth:
                              col.key === "__estado__"
                                ? 48
                                : col.key === "__comentario__"
                                  ? 230
                                  : 150,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {col.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rowsWithValidation.map(({ row, rowErrors, hasError, comentario }, idx) => {
                      const tooltipText =
                        row.no_encontrado
                          ? "No hay registros del RBD"
                          : Object.entries(rowErrors)
                              .map(([field, messages]) => `${field}: ${messages.join(" · ")}`)
                              .join(" | ");

                      return (
                        <TableRow
                          key={`${row.rbd}-${idx}`}
                          hover
                          sx={{
                            "& td": {
                              borderRight: "1px solid #D0D0D0",
                              borderBottom: "1px solid #D0D0D0",
                              fontSize: "0.78rem",
                              color: "#4A4A4A",
                              verticalAlign: "top",
                              whiteSpace: "nowrap",
                            },
                            backgroundColor: hasError ? "#F3DDD6" : "#FFFFFF",
                          }}
                        >
                          {tableColumns.map((col, index) => {
                            if (col.key === "__estado__") {
                              return (
                                <TableCell
                                  key={col.key}
                                  align="center"
                                  sx={{
                                    minWidth: 48,
                                    borderRight:
                                      index === tableColumns.length - 1 ? "none" : "1px solid #D0D0D0",
                                  }}
                                >
                                  <Tooltip title={hasError ? tooltipText : "Fila válida"} arrow>
                                    <Box sx={{ display: "inline-flex" }}>
                                      {hasError ? (
                                        <ErrorIcon sx={{ color: "#F05A57", fontSize: 22 }} />
                                      ) : (
                                        <CheckCircleIcon sx={{ color: "#59B85C", fontSize: 22 }} />
                                      )}
                                    </Box>
                                  </Tooltip>
                                </TableCell>
                              );
                            }

                            if (col.key === "__comentario__") {
                              return (
                                <TableCell
                                  key={col.key}
                                  sx={{
                                    minWidth: 230,
                                    whiteSpace: "normal",
                                    borderRight:
                                      index === tableColumns.length - 1 ? "none" : "1px solid #D0D0D0",
                                  }}
                                >
                                  {comentario}
                                </TableCell>
                              );
                            }

                            const cellErrors = rowErrors[col.key] || [];
                            const hasCellError = cellErrors.length > 0;

                            return (
                              <TableCell
                                key={col.key}
                                sx={{
                                  backgroundColor: hasCellError ? "#EBCBC0" : "inherit",
                                  borderRight:
                                    index === tableColumns.length - 1 ? "none" : "1px solid #D0D0D0",
                                }}
                                title={hasCellError ? cellErrors.join(" · ") : ""}
                              >
                                {formatCellValue(row[col.key], col.key)}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </PageBgBlock>
      </Box>
    </PageBody>
  );
}